import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  IconButton,
  Menu,
} from '@mui/material';
import { DataGrid, deDE, GridColDef } from '@mui/x-data-grid';
import { Add, MoreVert } from '@mui/icons-material';

import {
  ArnoLifecycleStatus,
  ArnoTemplateSource,
  IArnoDecisionTemplate,
  useArnoDecisionTemplates,
  useCloneArnoTemplateAsNative,
  useCreateArnoDraftVersion,
  useCreateArnoDecisionTemplate,
  useSetArnoTemplateLifecycle,
} from '../../hooks/reactQuerys/ArnoAdminQueries';
import { formattingSummary } from './migrationModel';
import { useNavigate } from 'react-router-dom';
import {
  assignedTypesLabel,
  assignmentSummary,
  lifecycleChip,
  LIFECYCLE_LABEL,
  templateSourceChip,
  TEMPLATE_SOURCE_LABEL,
  versionLabel,
} from './arnoAdminLabels';
import { TemplateAssignmentDialog } from './TemplateAssignmentDialog';
import { TemplateFlowDialog } from './TemplateFlowDialog';
import { useAlert } from '../../context/AlertContext';

/**
 * Entscheidungsbäume (#1245).
 *
 * Ein Entscheidungsbaum ist eine wiederverwendbare Logik zur Urkundenerzeugung,
 * keine Rechtsgeschäftsart. Er wird beliebig vielen DiNo-Rechtsgeschäftsarten
 * freigegeben, und eine Rechtsgeschäftsart darf mehrere Bäume haben.
 *
 * Die Liste zeigt beide Herkünfte nebeneinander: aus ArnoTop übernommen oder in
 * DiNo angelegt. Für die Runtime ist das kein Unterschied — der Unterschied
 * liegt darin, dass ein importierter Baum Legacy-Zuordnungen braucht und ein
 * eigener von Anfang an mit DiNo-Feldern arbeitet.
 */

export const DecisionTemplateTab: React.FC = () => {
  const { showAlert } = useAlert();
  const navigate = useNavigate();
  const [rowMenu, setRowMenu] = useState<{
    anchor: HTMLElement;
    row: IArnoDecisionTemplate;
  } | null>(null);
  const [sourceType, setSourceType] = useState<ArnoTemplateSource | ''>('');
  const [lifecycleStatus, setLifecycleStatus] = useState<
    ArnoLifecycleStatus | ''
  >('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<IArnoDecisionTemplate | null>(null);
  const [flowFor, setFlowFor] = useState<IArnoDecisionTemplate | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newGroup, setNewGroup] = useState('');

  const { decisionTemplates, isLoadingDecisionTemplates } =
    useArnoDecisionTemplates({
      sourceType: sourceType || undefined,
      lifecycleStatus: lifecycleStatus || undefined,
    });
  const { createDecisionTemplate, isCreatingDecisionTemplate } =
    useCreateArnoDecisionTemplate();
  const { setTemplateLifecycle, isSettingTemplateLifecycle } =
    useSetArnoTemplateLifecycle();
  const { createDraftVersion, isCreatingDraftVersion } =
    useCreateArnoDraftVersion();
  const { cloneTemplateAsNative, isCloningTemplate } =
    useCloneArnoTemplateAsNative();

  /**
   * Zum Editor: ein Entwurf wird direkt geöffnet, ein veröffentlichter Baum legt
   * vorher die nächste Version an — die veröffentlichte bleibt unverändert.
   */
  const openEditor = async (row: IArnoDecisionTemplate) => {
    try {
      if (row.publishedVersionId && row.publishedVersionId === row.versionId) {
        const draft = await createDraftVersion(row.idDecisionTemplate);
        showAlert({
          severity: 'success',
          text: `Version ${draft.versionNumber} als Entwurf angelegt.`,
        });
        navigate(`/admin/arnotop/editor/${draft.idDecisionTemplateVersion}`);
        return;
      }
      navigate(`/admin/arnotop/editor/${row.versionId}`);
    } catch (error) {
      showAlert({
        severity: 'error',
        text:
          error instanceof Error
            ? `Öffnen fehlgeschlagen: ${error.message}`
            : 'Öffnen fehlgeschlagen.',
      });
    }
  };

  /** Einen importierten Baum als DiNo-Entwurf kopieren; das Original bleibt stehen. */
  const clone = async (row: IArnoDecisionTemplate) => {
    try {
      const created = await cloneTemplateAsNative({
        idDecisionTemplate: row.idDecisionTemplate,
      });
      const { conversion } = created;
      const formatierung = formattingSummary(conversion.rich);
      showAlert({
        severity: 'success',
        duration: 12000,
        text: `${conversion.sections} Abschnitte und ${conversion.subBlocks} Unterbausteine kopiert: ${conversion.nodesDirect} übernommen, ${conversion.nodesAdapted} umgesetzt, ${conversion.nodesMigration} offen.${formatierung ? ` ${formatierung}` : ''}`,
      });
      navigate(`/admin/arnotop/editor/${created.idDecisionTemplateVersion}`);
    } catch (error) {
      showAlert({
        severity: 'error',
        text:
          error instanceof Error
            ? `Kopieren fehlgeschlagen: ${error.message}`
            : 'Kopieren fehlgeschlagen.',
      });
    }
  };

  // Die Suche bleibt in der Oberfläche: es sind Dutzende Zeilen, keine Tausende,
  // und ein Tastendruck soll keine Anfrage auslösen.
  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) {
      return decisionTemplates;
    }
    return decisionTemplates.filter(
      row =>
        row.name.toLowerCase().includes(needle) ||
        row.group.toLowerCase().includes(needle) ||
        row.assignments.some(entry =>
          (entry.legalTransactionType ?? '').toLowerCase().includes(needle)
        )
    );
  }, [decisionTemplates, search]);

  const create = async () => {
    try {
      const created = await createDecisionTemplate({
        name: newName.trim(),
        group: newGroup.trim() || undefined,
      });
      showAlert({
        severity: 'success',
        text: `${created.name} ist als Entwurf angelegt.`,
      });
      setCreateOpen(false);
      setNewName('');
      setNewGroup('');
      navigate(`/admin/arnotop/editor/${created.versionId}`);
    } catch (error) {
      showAlert({
        severity: 'error',
        text:
          error instanceof Error
            ? `Anlegen fehlgeschlagen: ${error.message}`
            : 'Anlegen fehlgeschlagen.',
      });
    }
  };

  const changeLifecycle = async (
    row: IArnoDecisionTemplate,
    next: ArnoLifecycleStatus
  ) => {
    try {
      await setTemplateLifecycle({
        idDecisionTemplate: row.idDecisionTemplate,
        lifecycleStatus: next,
      });
      showAlert({
        severity: 'success',
        text: `${row.name}: ${LIFECYCLE_LABEL[next]}.`,
      });
    } catch (error) {
      showAlert({
        severity: 'error',
        text:
          error instanceof Error
            ? `Ändern fehlgeschlagen: ${error.message}`
            : 'Ändern fehlgeschlagen.',
      });
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Entscheidungsbaum',
      flex: 3,
      minWidth: 300,
      renderCell: parameters => {
        const row = parameters.row as IArnoDecisionTemplate;
        return (
          <Box>
            <Typography variant='body2'>{row.name}</Typography>
            <Typography variant='caption' color='text.secondary'>
              {row.group || 'ohne Gruppe'} · {versionLabel(row)}
              {row.variants.length > 0 ? ` · ${row.variants.join(' / ')}` : ''}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'sourceType',
      headerName: 'Herkunft',
      width: 120,
      renderCell: parameters => {
        const chip = templateSourceChip(
          (parameters.row as IArnoDecisionTemplate).sourceType
        );
        return <Chip size='small' color={chip.color} label={chip.label} />;
      },
    },
    {
      field: 'assignedTypes',
      headerName: 'Freigegeben für',
      width: 210,
      renderCell: parameters => {
        const row = parameters.row as IArnoDecisionTemplate;
        const names = row.assignments
          .map(entry => entry.legalTransactionType)
          .filter(Boolean)
          .slice(0, 6)
          .join(', ');
        return (
          <Tooltip title={names || 'Noch keiner Rechtsgeschäftsart zugeordnet'}>
            <Box>
              <Typography variant='body2'>{assignedTypesLabel(row)}</Typography>
              {row.assignmentsMissing > 0 && (
                <Typography variant='caption' color='error'>
                  {row.assignmentsMissing} Ziel(e) fehlen
                </Typography>
              )}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: 'lifecycleStatus',
      headerName: 'Status',
      width: 130,
      renderCell: parameters => {
        const chip = lifecycleChip(
          (parameters.row as IArnoDecisionTemplate).lifecycleStatus
        );
        return <Chip size='small' color={chip.color} label={chip.label} />;
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 190,
      sortable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: parameters => {
        const row = parameters.row as IArnoDecisionTemplate;
        // Eine sichtbare Hauptaktion je Zeile, der Rest im Menü. Vorher standen hier fünf
        // gleichrangige Textknöpfe, und bei 1 440 px war der erste abgeschnitten
        // (»TSGESCHÄFTSARTEN«, Sichtprüfung Teil 40).
        return (
          <Stack
            direction='row'
            spacing={0.5}
            alignItems='center'
            justifyContent='flex-end'
          >
            {row.isNative ? (
              <Button
                size='small'
                variant='outlined'
                disabled={isCreatingDraftVersion}
                onClick={() => openEditor(row)}
              >
                Bearbeiten
              </Button>
            ) : (
              <Tooltip title='Als eigenen DiNo-Baum kopieren. Das Original bleibt unverändert; nicht sicher übersetzbare Stellen werden markiert.'>
                <span>
                  <Button
                    size='small'
                    variant='outlined'
                    disabled={isCloningTemplate}
                    onClick={() => clone(row)}
                  >
                    Kopieren
                  </Button>
                </span>
              </Tooltip>
            )}
            <Tooltip title='Weitere Aktionen'>
              <IconButton
                size='small'
                aria-label={`Weitere Aktionen für ${row.name}`}
                onClick={event =>
                  setRowMenu({ anchor: event.currentTarget, row })
                }
              >
                <MoreVert fontSize='small' />
              </IconButton>
            </Tooltip>
          </Stack>
        );
      },
    },
  ];

  const menuRow = rowMenu?.row ?? null;
  const closeRowMenu = () => setRowMenu(null);

  return (
    <Box>
      <Menu
        anchorEl={rowMenu?.anchor ?? null}
        open={Boolean(rowMenu)}
        onClose={closeRowMenu}
      >
        {menuRow && [
          <MenuItem
            key='types'
            onClick={() => {
              closeRowMenu();
              setSelected(menuRow);
            }}
          >
            Für Rechtsgeschäftsarten freigeben …
          </MenuItem>,
          <MenuItem
            key='flow'
            onClick={() => {
              closeRowMenu();
              setFlowFor(menuRow);
            }}
          >
            Ablauf ansehen
          </MenuItem>,
          menuRow.lifecycleStatus === 'DRAFT' ? (
            <MenuItem
              key='life'
              disabled={isSettingTemplateLifecycle}
              onClick={() => {
                closeRowMenu();
                changeLifecycle(menuRow, 'ACTIVE');
              }}
            >
              Aktivieren
            </MenuItem>
          ) : (
            <MenuItem
              key='life'
              disabled={isSettingTemplateLifecycle}
              onClick={() => {
                closeRowMenu();
                changeLifecycle(
                  menuRow,
                  menuRow.lifecycleStatus === 'ARCHIVED' ? 'ACTIVE' : 'ARCHIVED'
                );
              }}
            >
              {menuRow.lifecycleStatus === 'ARCHIVED'
                ? 'Reaktivieren'
                : 'Archivieren'}
            </MenuItem>
          ),
        ]}
      </Menu>
      <Alert severity='info' sx={{ mb: 2 }}>
        Ein Entscheidungsbaum ist eine Logik zur Urkundenerzeugung, keine
        Rechtsgeschäftsart. Derselbe Baum darf für viele
        DiNo-Rechtsgeschäftsarten gelten, und eine Rechtsgeschäftsart darf
        mehrere Bäume zur Auswahl haben.
      </Alert>

      <Stack
        direction='row'
        spacing={2}
        alignItems='center'
        sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}
      >
        <Typography variant='body2' sx={{ flexGrow: 1 }}>
          {assignmentSummary(decisionTemplates)}
        </Typography>
        <TextField
          size='small'
          label='Suche'
          value={search}
          onChange={event => setSearch(event.target.value)}
          sx={{ minWidth: 240 }}
        />
        <TextField
          size='small'
          select
          label='Herkunft'
          value={sourceType}
          onChange={event =>
            setSourceType(event.target.value as ArnoTemplateSource | '')
          }
          sx={{ minWidth: 160 }}
        >
          <MenuItem value=''>Alle</MenuItem>
          <MenuItem value='ARNOTOP_IMPORTED'>
            {TEMPLATE_SOURCE_LABEL.ARNOTOP_IMPORTED}
          </MenuItem>
          <MenuItem value='DINO_NATIVE'>
            {TEMPLATE_SOURCE_LABEL.DINO_NATIVE}
          </MenuItem>
        </TextField>
        <TextField
          size='small'
          select
          label='Status'
          value={lifecycleStatus}
          onChange={event =>
            setLifecycleStatus(event.target.value as ArnoLifecycleStatus | '')
          }
          sx={{ minWidth: 160 }}
        >
          <MenuItem value=''>Alle</MenuItem>
          <MenuItem value='ACTIVE'>{LIFECYCLE_LABEL.ACTIVE}</MenuItem>
          <MenuItem value='DRAFT'>{LIFECYCLE_LABEL.DRAFT}</MenuItem>
          <MenuItem value='ARCHIVED'>{LIFECYCLE_LABEL.ARCHIVED}</MenuItem>
        </TextField>
        <Button
          variant='contained'
          startIcon={<Add />}
          onClick={() => setCreateOpen(true)}
        >
          Neuer Entscheidungsbaum
        </Button>
      </Stack>

      <DataGrid
        autoHeight
        localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
        rows={rows}
        columns={columns}
        getRowId={row => row.idDecisionTemplate}
        loading={isLoadingDecisionTemplates}
        disableRowSelectionOnClick
        rowHeight={60}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        pageSizeOptions={[25, 50, 100]}
      />

      <TemplateAssignmentDialog
        template={selected}
        onClose={() => setSelected(null)}
      />

      <TemplateFlowDialog template={flowFor} onClose={() => setFlowFor(null)} />

      <Dialog
        open={createOpen}
        onClose={
          isCreatingDecisionTemplate ? undefined : () => setCreateOpen(false)
        }
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Neuer Entscheidungsbaum</DialogTitle>
        <DialogContent>
          <Alert severity='info' sx={{ mb: 2 }}>
            Der Entscheidungsbaum entsteht als Entwurf. Er arbeitet mit den
            Daten des Vorgangs und lässt sich veröffentlichen, sobald er
            vollständig ist.
          </Alert>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              size='small'
              label='Name'
              value={newName}
              onChange={event => setNewName(event.target.value)}
              disabled={isCreatingDecisionTemplate}
              autoFocus
            />
            <TextField
              size='small'
              label='Gruppe (optional)'
              value={newGroup}
              onChange={event => setNewGroup(event.target.value)}
              disabled={isCreatingDecisionTemplate}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCreateOpen(false)}
            disabled={isCreatingDecisionTemplate}
          >
            Abbrechen
          </Button>
          <Button
            variant='contained'
            onClick={create}
            disabled={isCreatingDecisionTemplate || !newName.trim()}
          >
            Erstellen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
